import {
  combineTransactionSteps,
  findChildrenInRange,
  getChangedRanges,
} from '@tiptap/core';
import { MarkType } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { getMarksOfTypeTouchedByPaddedRange } from '../utils/prosemirror';
import { hashtagInTextBlockRegex } from '../utils/hashtagRegex';
import { Link } from '../../extension-link/link';

type AutolinkOptions = {
  type: MarkType;
};

/**
 * Handles creating new hashtag marks from a '#' + content.
 */
export function hashtagMarkStarter(options: AutolinkOptions): Plugin {
  return new Plugin({
    key: new PluginKey('hashtag-mark-starter'),

    appendTransaction: (transactions, oldState, newState) => {
      const docChanges =
        transactions.some(transaction => transaction.docChanged) &&
        !oldState.doc.eq(newState.doc);

      if (!docChanges) {
        return;
      }

      const { tr } = newState;
      const transform = combineTransactionSteps(oldState.doc, [
        ...transactions,
      ]);

      const changes = getChangedRanges(transform);
      changes.forEach(({ newRange }) => {
        const nodesInChangedRanges = findChildrenInRange(
          newState.doc,
          newRange,
          node => node.isTextblock
        );

        const hashtags = getMarksOfTypeTouchedByPaddedRange(
          newState.doc,
          newRange,
          options.type
        );

        const links = getMarksOfTypeTouchedByPaddedRange(
          newState.doc,
          newRange,
          Link as any
        );

        // Create a hashtag mark when we see a `#` character plus an additional valid hashtag character
        if (
          !hashtags.length &&
          nodesInChangedRanges.length &&
          links.length === 0
        ) {
          const textBlock = nodesInChangedRanges[0];
          const text = newState.doc.textBetween(
            textBlock.pos,
            textBlock.pos + textBlock.node.nodeSize,
            undefined,
            ' '
          );

          const matchResults = [...text.matchAll(hashtagInTextBlockRegex)];
          if (matchResults) {
            matchResults.forEach(matchObject => {
              if (!matchObject.length) {
                return;
              }
              const match = matchObject[0];

              const matchIndex = matchObject.index;

              if (matchIndex !== undefined) {
                tr.addMark(
                  textBlock.pos + matchIndex + 1,
                  textBlock.pos + matchIndex + match.length + 1,
                  options.type.create({
                    'data-hashtag': match.slice(1),
                  })
                );
              }
            });
          }
        }
      });

      if (!tr.steps.length) {
        return;
      }

      return tr;
    },
  });
}
